import { useRouter } from "next/router";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";

import { ProductPageProps } from "app/smartphone/[slug]/[reference]/page";

type ProductData = Pick<ProductPageProps, "productData"> & {
  referenceSlug: string;
};

type ProductPageContextVariantData = {
  currentVariant?: Variant;
};

type ProductPageContextData =
  | (ProductPageProps["productData"] & ProductPageContextVariantData)
  | undefined;

export const ProductPageContext =
  createContext<ProductPageContextData>(undefined);

type ProductPageContextProps = ProductData;

type Variant = NonNullable<
  ProductPageContextProps["productData"]["variants"]
>[number];

export const ProductPageContextProvider = ({
  children,
  productData,
  referenceSlug,
}: PropsWithChildren<ProductPageContextProps>) => {
  const { query } = useRouter();

  const getInitialVariant = useCallback(
    () =>
      productData?.variants?.find(
        ({ variantSlug }: Variant) =>
          variantSlug === ((query?.reference as string) || referenceSlug),
      ),
    [productData?.variants, query?.reference, referenceSlug],
  );

  const [currentVariant, setCurrentVariant] = useState(getInitialVariant());

  useEffect(() => {
    setCurrentVariant(getInitialVariant());
  }, [getInitialVariant]);

  return (
    <ProductPageContext.Provider value={{ ...productData, currentVariant }}>
      {children}
    </ProductPageContext.Provider>
  );
};
